import * as React from "react";

import HelpIcon from "@mui/icons-material/Help";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { UserContext } from "context/usercontext";
import FetchIP from "src/common/hooks/FetchIP";
import PowerUserNotice from "src/domain/core/workflow/components/PowerUserNotice";
import { compareInstanceTypes } from "src/domain/core/workflow/utils/nodeSort";
import { ipClient } from "src/lib/ipClient";

const Row = styled("div")(() => ({
  marginBottom: "30px",
  display: "flex",
  justifyContent: "space-between",
}));

const RowCenter = styled("div")(() => ({
  textAlign: "center",

  svg: {
    width: "0.75em",
    height: "0.75em",
  },
}));

export interface WorkflowConfigFormInput {
  head_node_instance_type: MachineInstanceType;
  compute_instance_type: MachineInstanceType;
  initial_queue_size: number;
  max_queue_size: number;
  maintain_initial_size: boolean;
  access_from: string;
}

export interface WorkflowConfigFormError {
  initial_queue_size?: ValidationError;
  max_queue_size?: ValidationError;
  access_from?: ValidationError;
  head_node_instance_type?: ValidationError;
  compute_instance_type?: ValidationError;
}

export interface Actions {
  head_node_update_enabled: boolean;
  maintain_initial_size_enabled: boolean;
}

export type WorkflowConfigFormData = {
  head_node_instance_types: MachineInstanceType[];
  compute_instance_types: MachineInstanceType[];
  max_queue_size: number;
  maintain_initial_size: boolean;
  use_public_ips: boolean;
  scheduler: string;
  actions: Actions;
};

interface WorkflowFormProps {
  workflowData: WorkflowConfigFormData;
  values: WorkflowConfigFormInput;
  error: WorkflowConfigFormError;
  setValues: React.Dispatch<React.SetStateAction<WorkflowConfigFormInput>>;
  setError: React.Dispatch<React.SetStateAction<WorkflowConfigFormError>>;
}

export const WorkflowConfigForm: React.FC<WorkflowFormProps> = ({
  workflowData,
  error,
  values,
  setValues,
  setError,
}) => {
  const myUser = React.useContext(UserContext);

  const headNodeInstanceTypesSorted = workflowData?.head_node_instance_types?.sort(compareInstanceTypes);
  const computeNodeInstanceTypesSorted = workflowData?.compute_instance_types?.sort(compareInstanceTypes);

  const textfieldChange = (prop: keyof WorkflowConfigFormInput) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [prop]: event.target.type === "number" ? parseInt(event.target.value) : event.target.value,
    });
  };

  const selectChange = (event, prop) => {
    if (prop === "head_node_instance_type") {
      const instance = workflowData?.head_node_instance_types?.find((instance) => instance.name === event.target.value);
      if (instance) {
        setValues({ ...values, [prop]: instance });
      }
    }

    if (prop === "compute_instance_type") {
      const instance = workflowData?.compute_instance_types?.find((instance) => instance.name === event.target.value);
      if (instance) {
        setValues({ ...values, [prop]: instance });
      }
    }
  };

  const checkboxChange = (prop: keyof WorkflowConfigFormInput) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.checked });
  };

  const updateValue = (prop: keyof WorkflowConfigFormInput, value: string | number) => {
    setValues({ ...values, [prop]: value });
  };

  const updateError = (prop: keyof WorkflowConfigFormError, value: string | number) => {
    setError({ ...error, [prop]: value });
  };

  const fetchIP = FetchIP(ipClient, updateValue, updateError);

  React.useEffect(() => {
    // This is an intential design decision to bake the IP address fetching into the
    // workflow config form. This could have been a dependency in an effort to follow
    // the dependency inversion principle, but since the automatic fetching and support
    // for refreshing IP address is an integral part of this form it was decided that
    // this might be the best place for it. Future requirements, might prove otherwise.
    if (workflowData?.use_public_ips) {
      fetchIP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData?.use_public_ips]);

  const powerUser = myUser?.is_power_user;
  return (
    <>
      {workflowData.actions?.head_node_update_enabled && (
        <Row>
          <TextField
            required
            id="head-node-select"
            select
            label="Head Node"
            onChange={(e) => selectChange(e, "head_node_instance_type")}
            helperText={"Select the size of your head node"}
            value={
              headNodeInstanceTypesSorted ? values?.head_node_instance_type?.name || "" : "No instances configured"
            }
            variant="standard"
            error={error?.head_node_instance_type?.has}
            disabled={workflowData?.head_node_instance_types?.length === 0}
            sx={{ width: "100%" }}
          >
            {headNodeInstanceTypesSorted?.length > 0 ? (
              headNodeInstanceTypesSorted.map((option) => (
                <MenuItem key={option.instance_type} value={option.name}>
                  <Typography variant="body2">{option.name}</Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem key="" value="No instances configured">
                <Typography variant="body2">No instances configured</Typography>
              </MenuItem>
            )}
          </TextField>
        </Row>
      )}
      <Row>
        <TextField
          required
          id="compute-node-select"
          select
          label="Compute Node"
          value={computeNodeInstanceTypesSorted ? values?.compute_instance_type?.name || "" : "No instances configured"}
          onChange={(e) => selectChange(e, "compute_instance_type")}
          helperText="Select the size of your compute node"
          variant="standard"
          error={error?.compute_instance_type?.has}
          disabled={!(workflowData?.head_node_instance_types?.length > 0)}
          sx={{ width: "100%" }}
        >
          {computeNodeInstanceTypesSorted?.length > 0 ? (
            computeNodeInstanceTypesSorted.map((option) => (
              <MenuItem key={option.instance_type} value={option.name}>
                <Typography variant="body2">{option.name}</Typography>
              </MenuItem>
            ))
          ) : (
            <MenuItem key={"default"} value={"No instances configured"}>
              <Typography variant="body2">No instances configured</Typography>
            </MenuItem>
          )}
        </TextField>
      </Row>

      {workflowData?.use_public_ips && (
        <Row>
          <TextField
            required
            id="access-from"
            inputProps={{
              "data-testid": "access-from",
            }}
            label="Access From"
            variant="standard"
            value={values?.access_from}
            onChange={textfieldChange("access_from")}
            helperText={error?.access_from?.msg || "Your IP address - This has been auto-populated"}
            sx={{ width: "100%" }}
            error={error?.access_from?.has}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={fetchIP}
                >
                  <InputAdornment position="start">
                    <RefreshIcon />
                  </InputAdornment>
                </div>
              ),
            }}
          />
        </Row>
      )}

      <PowerUserNotice isPowerUser={powerUser}>
        <Row>
          <TextField
            required
            disabled={!powerUser}
            id="maximum-size"
            label="Maximum Cluster Size"
            type="number"
            variant="standard"
            inputProps={{
              "data-testid": "maximum-size",
              min: 1,
              max: workflowData?.max_queue_size,
            }}
            value={values?.max_queue_size ?? ""}
            helperText={
              error?.max_queue_size?.msg ||
              `Maximum cluster size (e.g. 10 compute nodes)
             Range: 1 - ${workflowData?.max_queue_size || "Loading..."}`
            }
            onChange={textfieldChange("max_queue_size")}
            sx={{ width: "100%", whiteSpace: "pre-line" }}
            error={error?.max_queue_size?.has}
          />

          <TextField
            id="initial-size"
            disabled={!powerUser}
            label="Initial Size"
            type="number"
            variant="standard"
            inputProps={{
              "data-testid": "initial-size",
              min: 0,
              max: workflowData?.max_queue_size,
            }}
            value={values?.initial_queue_size ?? ""}
            helperText={
              error?.initial_queue_size?.msg ||
              `Intial cluster size (e.g. 2 compute nodes) 
             Range: 0 - ${workflowData?.max_queue_size || "Loading..."}`
            }
            onChange={textfieldChange("initial_queue_size")}
            sx={{ width: "100%", whiteSpace: "pre-line" }}
            error={error?.initial_queue_size?.has}
          />
        </Row>

        {workflowData.actions?.maintain_initial_size_enabled && (
          <RowCenter>
            <FormControlLabel
              control={<Switch defaultChecked={workflowData?.maintain_initial_size} />}
              label="Maintain initial size"
              onChange={checkboxChange("maintain_initial_size")}
              sx={{ marginRight: 0 }}
              disabled={!powerUser}
            />

            <Tooltip title="Select to ensure cluster always has at least the inital size">
              <span>
                <IconButton size="small" disabled={!powerUser}>
                  <HelpIcon />
                </IconButton>
              </span>
            </Tooltip>
          </RowCenter>
        )}
      </PowerUserNotice>
    </>
  );
};
